import { useMemo, useState } from "react";
import {
  useCreateBooking,
  useSellPass,
  useListResourcePricing,
  useListInventoryPlans,
  useListSpaceResources,
  useListPlanSecondaryAccess,
  getListSpaceResourcesQueryKey,
  getListResourcePricingQueryKey,
  getListPlanSecondaryAccessQueryKey,
  type SpaceResource,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

// Account-scoped booking sheet — replaces the "head to /booking/new" friction.
// Locks accountId from the parent; otherwise mirrors the POST /booking contract
// the standalone /booking/new page uses (same hook, same server validation,
// same 409-on-conflict behavior).
interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: number;
  accountLabel: string;
  /** Called after the booking commits successfully so parent can invalidate lists. */
  onCreated?: () => void;
}

// Bookings are limited to rooms — Conference Room and Meeting Room (billed
// hourly). Workstations, cabins, and day passes are plans/memberships, not
// bookings, so they are excluded from both the pricing and the room pickers.
const BOOKABLE_RESOURCE_TYPES = new Set(["Conference_Room", "Meeting_Room"]);

type BookingMode = "room" | "pass";

// Upper bound on passes sold in one go — one seat picker is rendered per seat.
const MAX_PASS_SEATS = 20;

function formatINR(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n)}`;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

export function NewBookingSheet({ open, onOpenChange, accountId, accountLabel, onCreated }: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const createBooking = useCreateBooking();
  const sellPass = useSellPass();

  // ── Mode ──────────────────────────────────────────────────────────────────
  const [mode, setMode] = useState<BookingMode>("room");

  // ── Room state ─────────────────────────────────────────────────────────────
  const [resourcePricingId, setResourcePricingId] = useState<number | null>(null);
  const [spaceResourceId, setSpaceResourceId] = useState<number | null>(null);
  const [quantity, setQuantity] = useState<number>(1);
  const [discountPct, setDiscountPct] = useState<number>(0);
  const [memberClaimsGst, setMemberClaimsGst] = useState(false);

  // ── Pass state ─────────────────────────────────────────────────────────────
  const [passPlanId, setPassPlanId] = useState<number | null>(null);
  const [passDays, setPassDays] = useState<number>(1);
  // Seats = how many passes this sale covers (a pass is priced per person per day).
  const [passSeats, setPassSeats] = useState<number>(1);
  // One optional seat slot per pass; null = "assign later".
  const [passSeatIds, setPassSeatIds] = useState<(number | null)[]>([null]);
  // Additional discount % off the pass subtotal (server accepts discountPct).
  const [passDiscountPct, setPassDiscountPct] = useState<number>(0);
  // Payment mode collected at sale.
  const [passPaymentMode, setPassPaymentMode] = useState<"Cash" | "UPI">("Cash");
  // Start date of the pass — defaults to today (IST) but the operator can backdate
  // or future-date a sale. The server already accepts `startDate` and builds the
  // [startDate, startDate+days-1] window from it.
  const [passStart, setPassStart] = useState<string>(() =>
    new Date().toISOString().slice(0, 10),
  );

  // Pricing rows (all active for this space) — server validates plan match at booking time.
  const pricingParams = { status: "Active" as const };
  const { data: pricingRows = [] } = useListResourcePricing(pricingParams, {
    query: { enabled: open, queryKey: getListResourcePricingQueryKey(pricingParams) },
  });

  const { data: plans = [] } = useListInventoryPlans({});
  const planById = useMemo(
    () => Object.fromEntries(plans.map((p) => [p.id, p])),
    [plans],
  );

  // Only room pricing rows are bookable (Conference Room / Meeting Room).
  const bookablePricingRows = useMemo(
    () =>
      pricingRows.filter((p) =>
        BOOKABLE_RESOURCE_TYPES.has(planById[p.planId]?.resourceType ?? ""),
      ),
    [pricingRows, planById],
  );

  const selectedPricing = useMemo(
    () => pricingRows.find((p) => p.id === resourcePricingId) ?? null,
    [pricingRows, resourcePricingId],
  );

  // Seat picker — filtered by the picked plan's resourceType so the operator
  // only sees seats/rooms that match. Phase 21 ↔ Phase 23 link via 0056.
  // Pass mode also needs seats (Workstation), so enable when sheet is open in pass mode too.
  const { data: spaceResources = [] } = useListSpaceResources(undefined, {
    query: {
      enabled: open && (resourcePricingId != null || mode === "pass"),
      queryKey: getListSpaceResourcesQueryKey(),
    },
  });

  const plan = selectedPricing ? planById[selectedPricing.planId] : null;
  const planResourceType = plan?.resourceType ?? null;
  const isHourly = selectedPricing?.paymentUnit === "Hour";

  // Phase 24-06: fetch secondary access rows for the selected plan so the seat
  // picker includes resource types granted by cross-plan access (e.g., a
  // Workstation member can book Conference Rooms at a discount).
  const selectedPlanId = selectedPricing?.planId ?? null;
  const { data: secondaryAccessRows = [] } = useListPlanSecondaryAccess(selectedPlanId ?? 0, {
    query: {
      enabled: !!selectedPlanId,
      queryKey: getListPlanSecondaryAccessQueryKey(selectedPlanId ?? 0),
    },
  });
  const effectiveQty = isHourly ? quantity : 1;

  const filteredResources = useMemo(() => {
    if (!selectedPricing) return [];
    const active = spaceResources.filter((r: SpaceResource) => r.status === "Active");
    // Rooms only. Match the selected plan's resourceType (always Conference_Room
    // or Meeting_Room here); fall back to both room types if the plan has no
    // declared type.
    const allowed =
      planResourceType && BOOKABLE_RESOURCE_TYPES.has(planResourceType)
        ? new Set([planResourceType])
        : BOOKABLE_RESOURCE_TYPES;
    return active.filter(
      (r: SpaceResource) =>
        BOOKABLE_RESOURCE_TYPES.has(r.resourceType ?? "") &&
        allowed.has(r.resourceType ?? ""),
    );
  }, [selectedPricing, spaceResources, planResourceType]);

  // ── Pass derived data ──────────────────────────────────────────────────────
  const passPlans = useMemo(
    () => plans.filter((p) => p.cadence === "Non_Recurring" && (p.validityDays ?? 0) > 0),
    [plans],
  );
  const selectedPassPlan = useMemo(
    () => passPlans.find((p) => p.id === passPlanId) ?? null,
    [passPlans, passPlanId],
  );
  const workstationSeats = useMemo(
    () => spaceResources.filter((r) => r.resourceType === "Workstation" && r.status === "Active"),
    [spaceResources],
  );
  // Only the filled slots go to the server; the rest stay unassigned.
  const chosenSeatIds = useMemo(
    () => passSeatIds.slice(0, Math.max(1, passSeats)).filter((id): id is number => id != null),
    [passSeatIds, passSeats],
  );

  // Grow/shrink the seat slots with the seat count, keeping existing picks.
  // Capped at MAX_PASS_SEATS so a typo can't render hundreds of seat pickers.
  function changePassSeats(next: number) {
    const seats = Math.min(MAX_PASS_SEATS, Math.max(1, next));
    setPassSeats(seats);
    setPassSeatIds((prev) => {
      const kept = prev.slice(0, seats);
      return [...kept, ...Array<number | null>(seats - kept.length).fill(null)];
    });
  }

  function setSeatAt(index: number, id: number | null) {
    setPassSeatIds((prev) => {
      const next = [...prev];
      next[index] = id;
      return next;
    });
  }
  // Mirror pass-sale.ts exactly so the displayed total equals the stored one:
  //   base = monthlyPrice × days × seats; afterDiscount = base × (1 − discountPct/100);
  //   gst = afterDiscount × rate/100; total = afterDiscount + gst.
  const passBase = selectedPassPlan
    ? round2(
        Number(selectedPassPlan.monthlyPrice) * Math.max(1, passDays) * Math.max(1, passSeats),
      )
    : 0;
  const passDiscountAmt = round2((passBase * Math.min(100, Math.max(0, passDiscountPct))) / 100);
  const passTotal = round2(passBase - passDiscountAmt); // taxable after discount
  // Pass sales always carry GST — rate from the plan, else the standard 18%.
  // The server splits CGST/SGST vs IGST by place-of-supply, but total GST = rate × taxable.
  const passGstRate = selectedPassPlan ? Number(selectedPassPlan.taxRatePct ?? 18) : 0;
  const passGst = round2((passTotal * passGstRate) / 100);
  const passGrandTotal = round2(passTotal + passGst);

  // ── Reset ──────────────────────────────────────────────────────────────────
  function reset() {
    setMode("room");
    setResourcePricingId(null);
    setSpaceResourceId(null);
    setQuantity(1);
    setDiscountPct(0);
    setMemberClaimsGst(false);
    setPassPlanId(null);
    setPassDays(1);
    setPassSeats(1);
    setPassSeatIds([null]);
    setPassStart(new Date().toISOString().slice(0, 10));
    setPassDiscountPct(0);
    setPassPaymentMode("Cash");
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function onSubmit() {
    if (mode === "room") {
      if (!selectedPricing) {
        toast({ title: "Pick a resource pricing row first.", variant: "destructive" });
        return;
      }
      try {
        await createBooking.mutateAsync({
          data: {
            memberId: accountId,
            resourcePricingId: selectedPricing.id,
            quantity: effectiveQty,
            discountPct,
            memberClaimsGst,
            spaceResourceId: spaceResourceId ?? undefined,
          },
        });
        toast({ title: "Booking saved.", description: "Mark it Paid when payment is received." });
        // Refresh account-detail consumers (charges, memberships, occupancy)
        qc.invalidateQueries({ queryKey: ["accounts"] });
        qc.invalidateQueries({ queryKey: ["bookings"] });
        qc.invalidateQueries({ queryKey: ["resource-assignments"] });
        onCreated?.();
        reset();
        onOpenChange(false);
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          toast({
            title: "Seat / room conflict",
            description: "Already taken for this date or slot — pick another resource.",
            variant: "destructive",
          });
          return; // keep open so operator can pick another
        }
        toast({
          title: "Booking failed",
          description: (err as Error).message ?? "Try again.",
          variant: "destructive",
        });
      }
    } else {
      // Pass mode
      if (!passPlanId) {
        toast({ title: "Pick a pass plan first.", variant: "destructive" });
        return;
      }
      if (passDays < 1) {
        toast({ title: "Days must be at least 1.", variant: "destructive" });
        return;
      }
      if (!passStart) {
        toast({ title: "Pick a start date.", variant: "destructive" });
        return;
      }
      try {
        await sellPass.mutateAsync({
          data: {
            accountId,
            planId: passPlanId,
            startDate: passStart,
            days: Math.max(1, passDays),
            seats: Math.max(1, passSeats),
            discountPct: Math.min(100, Math.max(0, passDiscountPct)),
            seatResourceIds: chosenSeatIds.length > 0 ? chosenSeatIds : undefined,
            // Pass sales are always GST-charged at the plan rate.
            gstApplicable: true,
            paymentMode: passPaymentMode,
          },
        });
        toast({ title: "Pass sold.", description: "Charge is collectable under Transactions." });
        qc.invalidateQueries({ queryKey: ["accounts"] });
        qc.invalidateQueries({ queryKey: ["bookings"] });
        qc.invalidateQueries({ queryKey: ["resource-assignments"] });
        onCreated?.();
        reset();
        onOpenChange(false);
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const serverMsg =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
        if (status === 409) {
          toast({
            title: "Seat conflict",
            description: serverMsg ?? "That seat is already taken for this window — pick another.",
            variant: "destructive",
          });
          return;
        }
        if (status === 422) {
          toast({
            title: "Cannot sell pass",
            description: serverMsg ?? "No primary contact found on this account.",
            variant: "destructive",
          });
          return;
        }
        toast({
          title: "Pass sale failed",
          description: serverMsg ?? (err as Error).message ?? "Try again.",
          variant: "destructive",
        });
      }
    }
  }

  const planLabel = plan?.planName?.replace(/_/g, " ") ?? "—";
  const cadenceLabel = selectedPricing?.cadence ?? "—";

  const isSubmitDisabled =
    mode === "room"
      ? !selectedPricing || createBooking.isPending
      : passPlanId == null || passDays < 1 || !passStart || sellPass.isPending;
  const isPending = mode === "room" ? createBooking.isPending : sellPass.isPending;

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>New Booking</SheetTitle>
          <SheetDescription>
            For <span className="font-medium">{accountLabel}</span>. Stays in the Account context.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5 pt-4">
          {/* Mode toggle */}
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={mode === "room" ? "default" : "outline"}
              onClick={() => setMode("room")}
            >
              Room
            </Button>
            <Button
              type="button"
              size="sm"
              variant={mode === "pass" ? "default" : "outline"}
              onClick={() => setMode("pass")}
            >
              Pass
            </Button>
          </div>

          {/* ── Room mode ────────────────────────────────────────────────── */}
          {mode === "room" && (
            <>
              {/* Pricing picker */}
              <div className="space-y-1">
                <Label>Room Pricing</Label>
                <Select
                  value={resourcePricingId?.toString() ?? ""}
                  onValueChange={(v) => {
                    setResourcePricingId(Number(v));
                    setSpaceResourceId(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a Conference / Meeting Room rate" />
                  </SelectTrigger>
                  <SelectContent>
                    {bookablePricingRows.length === 0 ? (
                      <SelectItem value="0" disabled>
                        No Conference / Meeting Room pricing configured
                      </SelectItem>
                    ) : (
                      bookablePricingRows.map((p) => (
                        <SelectItem key={p.id} value={p.id.toString()}>
                          {(planById[p.planId]?.planName ?? `Plan #${p.planId}`).replace(/_/g, " ")} — {p.cadence}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {selectedPricing && (
                  <p className="text-xs text-muted-foreground">
                    {planLabel} · {cadenceLabel} · {selectedPricing.paymentUnit} pricing
                  </p>
                )}
              </div>

              {/* Hourly quantity */}
              {isHourly && (
                <div className="space-y-1">
                  <Label>Hours</Label>
                  <Input
                    type="number"
                    min={1}
                    value={quantity}
                    onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                    className="max-w-[120px]"
                  />
                </div>
              )}

              {/* Seat / room picker — filtered by plan.resourceType */}
              {selectedPricing && (
                <div className="space-y-1">
                  <Label>
                    {planResourceType === "Private_Cabin"
                      ? "Private Cabin"
                      : planResourceType === "Meeting_Room" || planResourceType === "Conference_Room"
                      ? "Room"
                      : "Workstation Seat"}
                  </Label>
                  <Select
                    value={spaceResourceId?.toString() ?? ""}
                    onValueChange={(v) => setSpaceResourceId(Number(v))}
                    disabled={filteredResources.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          filteredResources.length === 0
                            ? "No Active resources of this type"
                            : "Pick a resource (optional)"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredResources.map((r) => {
                        const sa = secondaryAccessRows.find((s) => s.resourceType === r.resourceType);
                        return (
                          <SelectItem key={r.id} value={r.id.toString()}>
                            <span className="flex items-center gap-2">
                              {r.label}
                              {sa && Number(sa.discountPct) === 100 && (
                                <Badge variant="outline" className="text-[10px]">Included</Badge>
                              )}
                              {sa && Number(sa.discountPct) > 0 && Number(sa.discountPct) < 100 && (
                                <Badge variant="outline" className="text-[10px]">{sa.discountPct}% off</Badge>
                              )}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">
                    Day-pass and pass plans hold a seat for that booking's date(s); recurring plans hold it indefinitely.
                    Leave blank to let staff assign later.
                  </p>
                </div>
              )}

              {/* Discount + GST claim — compact row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Discount %</Label>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    value={discountPct}
                    onChange={(e) => setDiscountPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="block">Member claims GST</Label>
                  <div className="flex h-9 items-center">
                    <Switch checked={memberClaimsGst} onCheckedChange={setMemberClaimsGst} />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Pass mode ────────────────────────────────────────────────── */}
          {mode === "pass" && (
            <>
              {/* Pass plan picker */}
              <div className="space-y-1">
                <Label>Pass Plan</Label>
                <Select
                  value={passPlanId?.toString() ?? ""}
                  onValueChange={(v) => {
                    const id = Number(v);
                    setPassPlanId(id);
                    const p = passPlans.find((x) => x.id === id);
                    if (p) setPassDays(p.validityDays ?? 1);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a pass plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {passPlans.length === 0 ? (
                      <SelectItem value="0" disabled>
                        No Non-Recurring pass plans configured
                      </SelectItem>
                    ) : (
                      passPlans.map((p) => (
                        <SelectItem key={p.id} value={p.id.toString()}>
                          {p.planName.replace(/_/g, " ")} — {formatINR(Number(p.monthlyPrice))}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {selectedPassPlan && (
                  <p className="text-xs text-muted-foreground">
                    {selectedPassPlan.planName.replace(/_/g, " ")} · {selectedPassPlan.validityDays ?? "—"} day window
                  </p>
                )}
              </div>

              {/* Start date */}
              <div className="space-y-1">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={passStart}
                  onChange={(e) => setPassStart(e.target.value)}
                  className="max-w-[200px]"
                />
              </div>

              {/* Days + seats. A pass is priced per person per day, so seats multiply the total. */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Days</Label>
                  <Input
                    type="number"
                    min={1}
                    value={passDays}
                    onChange={(e) => setPassDays(Math.max(1, Number(e.target.value) || 1))}
                    className="max-w-[120px]"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Seats</Label>
                  <Input
                    type="number"
                    min={1}
                    max={MAX_PASS_SEATS}
                    value={passSeats}
                    onChange={(e) => changePassSeats(Number(e.target.value) || 1)}
                    className="max-w-[120px]"
                  />
                </div>
              </div>
              <p className="-mt-3 text-xs text-muted-foreground">
                Pass covers {passDays} day{passDays !== 1 ? "s" : ""}
                {passStart ? ` starting ${passStart}` : ""} for {passSeats}{" "}
                {passSeats !== 1 ? "people" : "person"}.
              </p>

              {/* Additional discount % off the pass subtotal */}
              <div className="space-y-1">
                <Label>Additional Discount (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={passDiscountPct}
                  onChange={(e) =>
                    setPassDiscountPct(Math.min(100, Math.max(0, Number(e.target.value) || 0)))
                  }
                  className="max-w-[120px]"
                />
              </div>

              {/* Workstation seat pickers — one slot per seat, optional. A seat
                  already picked in another slot is hidden so no seat is doubled. */}
              <div className="space-y-2">
                <Label>
                  {passSeats === 1 ? "Workstation Seat (optional)" : "Workstation Seats (optional)"}
                </Label>
                {Array.from({ length: passSeats }).map((_, i) => {
                  const takenElsewhere = new Set(
                    passSeatIds.filter((id, j) => id != null && j !== i) as number[],
                  );
                  return (
                    <div key={i} className="flex items-center gap-2">
                      {passSeats > 1 && (
                        <span className="w-14 shrink-0 text-xs text-muted-foreground">
                          Seat {i + 1}
                        </span>
                      )}
                      <Select
                        value={passSeatIds[i]?.toString() ?? "none"}
                        onValueChange={(v) => setSeatAt(i, v === "none" ? null : Number(v))}
                        disabled={workstationSeats.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              workstationSeats.length === 0
                                ? "No Active workstations"
                                : "No seat (assign later)"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No seat (assign later)</SelectItem>
                          {workstationSeats
                            .filter((r) => !takenElsewhere.has(r.id))
                            .map((r) => (
                              <SelectItem key={r.id} value={r.id.toString()}>
                                {r.label}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>

              {/* Payment mode. GST always applies to a pass sale, so both modes are open. */}
              <div className="space-y-1">
                <Label className="block">Payment Mode</Label>
                <Select
                  value={passPaymentMode}
                  onValueChange={(v) => setPassPaymentMode(v as "Cash" | "UPI")}
                >
                  <SelectTrigger className="sm:w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Computed total — plain markup per project memory (no FormLabel/FormField needed) */}
              {selectedPassPlan && (
                <div className="rounded-md border p-3 text-sm space-y-1">
                  <div className="flex justify-between text-muted-foreground">
                    <span>
                      Subtotal
                      <span className="ml-1 text-xs">
                        ({formatINR(Number(selectedPassPlan.monthlyPrice))} × {passDays} day{passDays !== 1 ? "s" : ""}
                        {passSeats > 1 ? ` × ${passSeats} seats` : ""})
                      </span>
                    </span>
                    <span className="tabular-nums">{formatINR(passBase)}</span>
                  </div>
                  {passDiscountPct > 0 && (
                    <div className="flex justify-between text-muted-foreground">
                      <span>Discount ({passDiscountPct}%)</span>
                      <span className="tabular-nums">−{formatINR(passDiscountAmt)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-muted-foreground">
                    <span>GST ({passGstRate}%)</span>
                    <span className="tabular-nums">{formatINR(passGst)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-1 text-base font-semibold">
                    <span>Total</span>
                    <span className="tabular-nums">{formatINR(passGrandTotal)}</span>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex justify-end gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancel
            </Button>
            <Button onClick={onSubmit} disabled={isSubmitDisabled}>
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "pass" ? "Sell Pass" : "Create Booking"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
